import { rationalDecimal } from './fractionExplorerModel';
export type Q = { n: bigint; d: bigint };
const gcd = (a: bigint, b: bigint): bigint => (b ? gcd(b, a % b) : a < 0n ? -a : a);
export function q(n: bigint, d = 1n): Q {
  if (!d) throw new Error('Division by zero is undefined.');
  if (d < 0n) {
    n = -n;
    d = -d;
  }
  const g = gcd(n, d);
  return { n: n / g, d: d / g };
}
export const add = (a: Q, b: Q) => q(a.n * b.d + b.n * a.d, a.d * b.d);
export const neg = (a: Q) => q(-a.n, a.d);
export const sub = (a: Q, b: Q) => add(a, neg(b));
export const mul = (a: Q, b: Q) => q(a.n * b.n, a.d * b.d);
export const div = (a: Q, b: Q) => q(a.n * b.d, a.d * b.n);
export const number = (a: Q) => Number(a.n) / Number(a.d);
export const text = (a: Q) => (a.d === 1n ? String(a.n) : `${a.n}/${a.d}`);
export function shown(a: Q) {
  const r = rationalDecimal({ numerator: a.n < 0n ? -a.n : a.n, denominator: a.d });
  return `${r.exact ? '=' : '≈'} ${a.n < 0n ? '-' : ''}${r.text}`;
}
export const forms = (value: Q) =>
  value.d === 1n ? text(value) : `${text(value)} ${shown(value)}`;
export const approx = (v: number) => Number(v.toPrecision(8)).toString();
export function read(text: string | undefined, label: string, max = 9999): Q {
  if (!text?.trim()) throw new Error(`Enter ${label}. Your input has been kept.`);
  const v = Number(text);
  if (!Number.isFinite(v) || Math.abs(v) > max)
    throw new Error(`${label} must be from −${max} to ${max}. Your input has been kept.`);
  const m = text.trim().match(/^([+-]?)(\d*\.?\d+)(?:e([+-]?\d+))?$/i);
  if (!m) throw new Error(`Enter a valid number for ${label}.`);
  const [whole, decimal = ''] = m[2].split('.');
  const places = decimal.length - Number(m[3] ?? 0);
  if (places > 6 || places < -10)
    throw new Error(`${label} supports up to 6 decimal places. Your input has been kept.`);
  const n = BigInt((whole || '0') + decimal) * (m[1] === '-' ? -1n : 1n);
  return places >= 0 ? q(n, 10n ** BigInt(places)) : q(n * 10n ** BigInt(-places));
}
export type Step = { title: string; formula: string; explanation: string };
export type Line = { a: Q; b: Q; c: Q };
const zero = q(0n),
  two = q(2n),
  four = q(4n);
export const lineText = (e: Line) => `(${text(e.a)})x + (${text(e.b)})y = ${text(e.c)}`;
export function linearLearning(draft: Record<string, string>) {
  const a = read(draft.a, 'x multiplier'),
    b = read(draft.b, 'Added value'),
    c = read(draft.c, 'Right side'),
    input = read(draft.x, 'Machine input');
  const remaining = sub(c, b),
    root = a.n ? div(remaining, a) : null;
  const output = add(mul(a, input), b);
  const kind = root ? 'unique' : remaining.n ? 'none' : 'infinite';
  const steps: Step[] = [
    {
      title: 'Read both sides',
      formula: `(${text(a)})x + (${text(b)}) = ${text(c)}`,
      explanation:
        'The right side is the number from your question. The unknown x is calculated from it.',
    },
    {
      title: 'Undo the added value',
      formula: `(${text(a)})x = ${text(c)} − (${text(b)}) = ${text(remaining)}`,
      explanation:
        'Subtract the same added value from both sides. Subtracting a negative value adds its magnitude.',
    },
    root
      ? {
          title: 'Divide both sides',
          formula: `x = (${text(remaining)}) ÷ (${text(a)}) = ${forms(root)}`,
          explanation:
            'The multiplier is nonzero, so division gives one solution. Parentheses in decimal digits mark a repeating block.',
        }
      : {
          title: 'Check the zero multiplier',
          formula: `0x = ${text(remaining)}`,
          explanation: remaining.n
            ? 'Zero times any number is zero. This nonzero right side makes the equation impossible.'
            : 'Both sides are equal for every real x. Do not divide by zero.',
        },
    root
      ? {
          title: 'Substitute to check',
          formula: `(${text(a)}) × (${text(root)}) + (${text(b)}) = ${text(add(mul(a, root), b))} = ${text(c)}`,
          explanation:
            'The exact value satisfies the original equation. The graph meets the horizontal right-side line here.',
        }
      : {
          title: 'Connect to the graph',
          formula:
            kind === 'none'
              ? 'Distinct horizontal lines: no intersection'
              : 'Identical horizontal lines: every x works',
          explanation: 'A zero multiplier is a valid mathematical case, not an input to replace.',
        },
    {
      title: 'Run the function forwards',
      formula: `f(${text(input)}) = (${text(a)}) × (${text(input)}) + (${text(b)}) = ${forms(output)}`,
      explanation:
        'Evaluation starts from an input. Solving starts from an output and works backwards.',
    },
  ];
  return {
    a,
    b,
    c,
    input,
    output,
    root,
    kind,
    steps,
    result: root
      ? `x = ${forms(root)}`
      : kind === 'none'
        ? 'No solution'
        : 'Every real x is a solution',
    interpretation: root
      ? 'The intersection solves the equation; the separate machine input evaluates the same rule.'
      : kind === 'none'
        ? 'Parallel constant functions have no intersection.'
        : 'Both constant functions coincide.',
  };
}
export function systemLearning(
  drafts: Record<string, string>[],
  method: 'elimination' | 'substitution',
  first: 'x' | 'y',
) {
  const lines = drafts.map((d, i) => ({
    a: read(d.a, `Equation ${i + 1} x coefficient`),
    b: read(d.b, `Equation ${i + 1} y coefficient`),
    c: read(d.c, `Equation ${i + 1} result`),
  }));
  const steps: Step[] = [
    {
      title: 'Read the system',
      formula: lines.map((e, i) => `E${i + 1}: ${lineText(e)}`).join('; '),
      explanation:
        'A common solution must satisfy every equation, including any zero-coefficient equation.',
    },
  ];
  const contradiction = lines.findIndex((e) => !e.a.n && !e.b.n && e.c.n !== 0n);
  let kind: 'unique' | 'none' | 'infinite' = 'infinite',
    x: Q | null = null,
    y: Q | null = null;
  let basis: number[] = [];
  if (contradiction >= 0) {
    kind = 'none';
    steps.push({
      title: 'Find a contradiction',
      formula: `E${contradiction + 1}: 0 = ${text(lines[contradiction].c)}`,
      explanation: 'This equation cannot be true for any x or y.',
    });
  } else {
    outer: for (let i = 0; i < lines.length; i++)
      for (let j = i + 1; j < lines.length; j++) {
        const e = lines[i],
          f = lines[j],
          det = sub(mul(e.a, f.b), mul(f.a, e.b));
        if (det.n) {
          basis = [i, j];
          x = div(sub(mul(e.c, f.b), mul(f.c, e.b)), det);
          y = div(sub(mul(e.a, f.c), mul(f.a, e.c)), det);
          kind = 'unique';
          const target = first === 'x' ? 'a' : 'b',
            other = first === 'x' ? 'b' : 'a',
            otherName = first === 'x' ? 'y' : 'x';
          if (method === 'elimination') {
            if (e[target].n === 0n && f[target].n === 0n)
              throw new Error('Choose a nonzero coefficient to eliminate.');
            const left = sub(mul(f[target], e[other]), mul(e[target], f[other])),
              right = sub(mul(f[target], e.c), mul(e[target], f.c));
            steps.push(
              {
                title: `Eliminate ${first}`,
                formula: `(${text(f[target])}) × E${i + 1} − (${text(e[target])}) × E${j + 1}: (${text(left)})${otherName} = ${text(right)}`,
                explanation:
                  'Multiply the entire equations, then subtract. The chosen variable cancels on both sides.',
              },
              {
                title: `Solve for ${otherName}`,
                formula: `${otherName} = (${text(right)}) ÷ (${text(left)}) = ${text(first === 'x' ? y : x)}`,
                explanation: 'Divide by the remaining nonzero coefficient.',
              },
            );
          } else {
            const k = e[target].n ? i : j,
              l = k === i ? j : i,
              u = lines[k],
              v = lines[l];
            const coefficient = sub(v[other], div(mul(v[target], u[other]), u[target])),
              right = sub(v.c, div(mul(v[target], u.c), u[target]));
            steps.push(
              {
                title: `Isolate ${first}`,
                formula: `E${k + 1}: ${first} = (${text(u.c)} − (${text(u[other])})${otherName}) / (${text(u[target])})`,
                explanation:
                  'Choose an equation with a nonzero coefficient for the variable you isolate.',
              },
              {
                title: 'Substitute into the other equation',
                formula: `E${l + 1}: (${text(v[target])}) × ((${text(u.c)} − (${text(u[other])})${otherName}) / (${text(u[target])}) + (${text(v[other])})${otherName} = ${text(v.c)}; (${text(coefficient)})${otherName} = ${text(right)}; ${otherName} = ${text(first === 'x' ? y : x)}`,
                explanation:
                  'Replace the isolated variable, expand, collect like terms and divide.',
              },
            );
          }
          const foundOther = first === 'x' ? y : x,
            chosen = e[target].n ? e : f;
          steps.push({
            title: `Recover ${first}`,
            formula: `${first} = (${text(chosen.c)} − (${text(chosen[other])}) × (${text(foundOther)})) / (${text(chosen[target])}) = ${text(first === 'x' ? x : y)}`,
            explanation: 'Substitute the first solved value into an original equation.',
          });
          break outer;
        }
      }
    if (x && y) {
      lines.forEach((e, i) => {
        const lhs = add(mul(e.a, x!), mul(e.b, y!)),
          ok = sub(lhs, e.c).n === 0n;
        if (!ok) kind = 'none';
        steps.push({
          title: `Check Equation ${i + 1}`,
          formula: `(${text(e.a)}) × (${text(x!)}) + (${text(e.b)}) × (${text(y!)}) = ${text(lhs)} ${ok ? '=' : '≠'} ${text(e.c)}`,
          explanation: ok
            ? 'The exact candidate satisfies this equation.'
            : 'This equation rejects the candidate. The whole system has no common solution.',
        });
      });
    } else {
      const i = lines.findIndex((e) => e.a.n || e.b.n);
      if (i < 0)
        steps.push({
          title: 'Recognize identities',
          formula: 'Every equation is 0 = 0',
          explanation:
            'Every real ordered pair (x, y) is a solution. These equations describe the whole plane.',
        });
      else {
        const e = lines[i],
          key = e.a.n ? 'a' : 'b';
        lines.forEach((f, j) => {
          if (j === i) return;
          const k = div(f[key], e[key]),
            delta = sub(f.c, mul(k, e.c));
          if (delta.n) kind = 'none';
          steps.push({
            title: `Compare Equation ${j + 1}`,
            formula: `E${j + 1} − (${text(k)}) × E${i + 1}: 0 = ${text(delta)}`,
            explanation: delta.n
              ? 'The coefficients are proportional but the constants disagree. There is no common solution.'
              : 'This equation adds no new restriction. The nonzero equations describe the same line.',
          });
        });
      }
    }
  }
  return {
    lines,
    kind,
    x,
    y,
    basis,
    steps,
    result:
      kind === 'unique'
        ? `(x, y) = (${text(x!)}, ${text(y!)})`
        : kind === 'none'
          ? 'No common solution'
          : 'Infinitely many solutions',
    interpretation:
      kind === 'unique'
        ? 'The marked intersection satisfies every equation.'
        : kind === 'none'
          ? 'There is no point shared by every equation. A pairwise intersection alone is not enough.'
          : lines.every((e) => !e.a.n && !e.b.n)
            ? 'All equations are identities: the entire plane is allowed.'
            : 'The nonzero equations coincide: all points on the same line are solutions.',
  };
}
function integerRoot(n: bigint): bigint {
  if (n < 2n) return n;
  let x = n,
    y = (x + 1n) / 2n;
  while (y < x) {
    x = y;
    y = (x + n / x) / 2n;
  }
  return x;
}
export function functionLearning(kind: 'linear' | 'quadratic', draft: Record<string, string>) {
  const a = read(draft.a, 'Coefficient a'),
    b = read(draft.b, 'Coefficient b'),
    c = kind === 'quadratic' ? read(draft.c, 'Constant c') : zero,
    x = read(draft.x, 'Input x', 1000);
  const at = (v: Q) =>
    kind === 'linear' ? add(mul(a, v), b) : add(add(mul(a, mul(v, v)), mul(b, v)), c);
  const y = at(x),
    effectiveA = kind === 'linear' ? zero : a,
    effectiveB = kind === 'linear' ? a : b,
    constant = kind === 'linear' ? b : c;
  const rule =
    kind === 'linear'
      ? `y = (${text(a)})x + (${text(b)})`
      : `y = (${text(a)})x² + (${text(b)})x + (${text(c)})`;
  const steps: Step[] = [
    {
      title: 'Read the function',
      formula: rule,
      explanation:
        'A function maps each real input x to exactly one output y in this polynomial model.',
    },
    {
      title: 'Substitute your input',
      formula:
        kind === 'linear'
          ? `f(${text(x)}) = (${text(a)}) × (${text(x)}) + (${text(b)})`
          : `f(${text(x)}) = (${text(a)}) × (${text(x)})² + (${text(b)}) × (${text(x)}) + (${text(c)})`,
      explanation:
        'Use parentheses around negative numbers. Square the input before multiplying its coefficient.',
    },
  ];
  if (kind === 'quadratic')
    steps.push({
      title: 'Evaluate each term',
      formula: `x² = ${text(mul(x, x))}; ax² = ${text(mul(a, mul(x, x)))}; bx = ${text(mul(b, x))}`,
      explanation: 'For example, (−2)² = 4. This is different from −2².',
    });
  steps.push({
    title: 'Combine the terms',
    formula: `f(${text(x)}) = ${forms(y)}`,
    explanation: 'The exact fraction and plotted point describe the same output.',
  });
  steps.push({
    title: 'Find the y-intercept',
    formula: `f(0) = ${text(constant)}; y-intercept (0, ${text(constant)})`,
    explanation: 'Set x to zero. All variable terms become zero, leaving the constant term.',
  });
  let rootSummary = '',
    roots: number[] = [],
    vertex: { x: Q; y: Q } | null = null,
    interpretation = '';
  if (!effectiveA.n) {
    if (effectiveB.n) {
      const root = div(neg(constant), effectiveB);
      roots = [number(root)];
      rootSummary = `x = ${forms(root)}`;
      interpretation =
        effectiveB.n > 0n
          ? 'The line rises from left to right.'
          : 'The line falls from left to right.';
      steps.push({
        title: 'Find the x-intercept',
        formula: `0 = (${text(effectiveB)})x + (${text(constant)}); x = ${forms(root)}`,
        explanation: 'Set y to zero and solve the resulting linear equation.',
      });
    } else {
      interpretation = 'This is a constant function. It has no turning point.';
      rootSummary = constant.n ? 'No real roots' : 'Every real x is a solution';
      steps.push({
        title: 'Recognize a constant function',
        formula: `y = ${text(constant)}; ${constant.n ? 'no x-intercept' : 'every x is an x-intercept'}`,
        explanation:
          'Zero variable coefficients remove the variable terms. A constant graph is a horizontal line.',
      });
    }
  } else {
    const h = div(neg(b), mul(two, a));
    vertex = { x: h, y: at(h) };
    const disc = sub(mul(b, b), mul(four, mul(a, c)));
    interpretation = `The parabola opens ${a.n > 0n ? 'upwards' : 'downwards'}, with a ${a.n > 0n ? 'minimum' : 'maximum'} at (${text(h)}, ${text(vertex.y)}).`;
    steps.push(
      {
        title: 'Find the turning point',
        formula: `h = −b/(2a) = ${text(h)}; k = f(h) = ${text(vertex.y)}`,
        explanation: interpretation,
      },
      {
        title: 'Test the discriminant',
        formula: `D = b² − 4ac = (${text(b)})² − 4 × (${text(a)}) × (${text(c)}) = ${text(disc)}`,
        explanation:
          disc.n < 0n
            ? 'D < 0: no real x-intercepts.'
            : disc.n === 0n
              ? 'D = 0: one repeated real root.'
              : 'D > 0: two distinct real x-intercepts.',
      },
    );
    if (disc.n < 0n) rootSummary = 'No real roots';
    if (disc.n >= 0n) {
      const A = number(a),
        B = number(b),
        C = number(c),
        D = number(disc);
      if (!disc.n) roots = [number(h)];
      else {
        const t = -0.5 * (B + (B >= 0 ? 1 : -1) * Math.sqrt(D));
        roots = [t / A, C / t].sort((u, v) => u - v);
      }
      const rn = integerRoot(disc.n),
        rd = integerRoot(disc.d);
      const exactRoots =
        rn * rn === disc.n && rd * rd === disc.d
          ? [div(sub(neg(b), q(rn, rd)), mul(two, a)), div(add(neg(b), q(rn, rd)), mul(two, a))]
          : null;
      rootSummary = exactRoots
        ? `x = ${[...new Set(exactRoots.map(text))].join(' or ')}`
        : `x ≈ ${roots.map(approx).join(' or ')}`;
      steps.push({
        title: 'Solve y = 0',
        formula: `x = (−b ± √D)/(2a) = (−(${text(b)}) ± √(${text(disc)}))/(2 × (${text(a)})); ${rootSummary}`,
        explanation:
          'Irrational roots use ≈ and eight significant figures. Graph points use unrounded values; exact substitution evaluates the chosen input.',
      });
      steps.push({
        title: 'Check the roots',
        formula: exactRoots
          ? [...new Map(exactRoots.map((r) => [text(r), r])).values()]
              .map(
                (r) =>
                  `f(${text(r)}) = (${text(a)}) × (${text(r)})² + (${text(b)}) × (${text(r)}) + (${text(c)}) = ${text(at(r))}`,
              )
              .join('; ')
          : roots
              .map(
                (r) =>
                  `For α ≈ ${approx(r)}, f(α) ≈ ${approx(number(a) * r * r + number(b) * r + number(c))}`,
              )
              .join('; '),
        explanation: exactRoots
          ? 'Substitute each exact root into the original polynomial. Each output is exactly zero.'
          : 'This numerical check uses unrounded computed roots. Small residuals can arise from floating-point arithmetic; the quadratic formula supplies the algebraic solution.',
      });
    }
  }
  const center = vertex ? number(vertex.x) : 0;
  const extent = Math.min(
    1000000,
    Math.max(
      5,
      Math.abs(number(x)),
      Math.abs(center) + 3,
      ...roots.filter((v) => Math.abs(v) <= 1000000).map((v) => Math.abs(v) + 1),
    ),
  );
  const points = Array.from({ length: 161 }, (_, i) => {
    const v = -extent + (2 * extent * i) / 160;
    return {
      x: v,
      y:
        kind === 'linear'
          ? number(a) * v + number(b)
          : number(a) * v * v + number(b) * v + number(c),
    };
  });
  const table = [...new Set([-2, -1, 0, 1, 2, number(x)])]
    .sort((u, v) => u - v)
    .map((v) => ({ x: v, y: at(read(String(v), 'Table input', 1000)) }));
  return {
    rule,
    rootSummary,
    steps,
    result: `f(${text(x)}) = ${forms(y)}`,
    interpretation,
    roots,
    vertex,
    points,
    target: { x: number(x), y: number(y) },
    table,
    xLabel: 'Input x',
    yLabel: 'Output y',
    discrete: false,
  };
}
export function linearPlot(r: ReturnType<typeof linearLearning>) {
  const extent = Math.min(
    1000000,
    Math.max(
      5,
      Math.abs(number(r.input)),
      r.root ? Math.min(1000000, Math.abs(number(r.root)) + 1) : 0,
    ),
  );
  return {
    points: [
      { x: -extent, y: number(r.a) * -extent + number(r.b) },
      { x: extent, y: number(r.a) * extent + number(r.b) },
    ],
    target: {
      x: r.root ? number(r.root) : number(r.input),
      y: r.root ? number(r.c) : number(r.output),
    },
    interpretation: r.interpretation,
    xLabel: 'x',
    yLabel: 'ax + b',
    discrete: false,
  };
}
